import { NumberInput } from '@/components/Inputs/NumberInput';
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BodyText } from './BodyText';
import { MarkedSlider } from './MarkedSlider';

export const MatchRangeSlider = ({
  onMinThresholdChange,
  onMaxThresholdChange,
  minThreshold,
  maxThreshold,
}: {
  onMinThresholdChange: (value: number) => void;
  onMaxThresholdChange: (value: number) => void;
  minThreshold: number;
  maxThreshold: number;
}) => {
  const [localMinThreshold, setLocalMinThreshold] = useState(minThreshold);
  const [localMaxThreshold, setLocalMaxThreshold] = useState(maxThreshold);
  const debounceReference = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (minThreshold !== localMinThreshold && !debounceReference.current) {
      setLocalMinThreshold(minThreshold);
    }
    if (maxThreshold !== localMaxThreshold && !debounceReference.current) {
      setLocalMaxThreshold(maxThreshold);
    }
  }, [minThreshold, maxThreshold, localMinThreshold, localMaxThreshold]);

  const debouncedHandleThresholdChange = useCallback(
    (minValue: number, maxValue: number) => {
      if (debounceReference.current) {
        clearTimeout(debounceReference.current);
      }
      debounceReference.current = setTimeout(() => {
        onMinThresholdChange(minValue);
        onMaxThresholdChange(maxValue);
      }, 300);
    },
    [onMinThresholdChange, onMaxThresholdChange]
  );

  const handleSliderChange = (value: number | number[]) => {
    const [min, max] = value as number[];
    setLocalMinThreshold(min);
    setLocalMaxThreshold(max);
    debouncedHandleThresholdChange(min, max);
  };

  const handleMinInputChange = (value: number) => {
    const newMin = Math.min(value, localMaxThreshold);
    setLocalMinThreshold(newMin);
    debouncedHandleThresholdChange(newMin, localMaxThreshold);
  };

  const handleMaxInputChange = (value: number) => {
    const newMax = Math.max(value, localMinThreshold);
    setLocalMaxThreshold(newMax);
    debouncedHandleThresholdChange(localMinThreshold, newMax);
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' gap='6px'>
      <Box display='flex' gap='8px' alignItems='center'>
        <NumberInput
          value={localMinThreshold}
          onChange={handleMinInputChange}
          decimalPlaces={2}
          min={1}
          max={localMaxThreshold}
          slotProps={{ input: { endAdornment: <BodyText>%</BodyText> } }}
          style={{ width: '93px' }}
        />
        <BodyText>to</BodyText>
        <NumberInput
          value={localMaxThreshold}
          onChange={handleMaxInputChange}
          decimalPlaces={2}
          min={localMinThreshold}
          max={100}
          slotProps={{ input: { endAdornment: <BodyText>%</BodyText> } }}
          style={{ width: '93px' }}
        />
      </Box>
      <MarkedSlider
        sx={{
          minWidth: '186px',
          '& .MuiSlider-mark': { backgroundColor: 'secondary.main' },
          '& .MuiSlider-track': { backgroundColor: 'secondary.main' },
          '& .MuiSlider-rail': { backgroundColor: 'action.disabledBackground' },
        }}
        min={1}
        max={100}
        value={[localMinThreshold, localMaxThreshold]}
        onChange={(_, value) => handleSliderChange(value)}
        valueLabelDisplay='off'
      />
    </Box>
  );
};
